import axios from "axios";
import router from "./router";
import { coalesceReads } from "./utils/coalesceReads";
import {
  buildSessionExpiredError,
  clearStoredSession,
  getStoredAccessData,
  hasValidSession,
  isSessionExpiredError,
} from "./publicjs/authSession";

const REQUEST_TIMEOUT = Number(process.env.VUE_APP_API_TIMEOUT || 60000);
const PUBLIC_REQUESTS = [
  { path: "login" },
  { path: "empresa", methods: ["get"] },
  { path: "turnos", methods: ["get"] },
  { path: "taller/publico", methods: ["get"] },
];

const isPublicRequest = (config = {}) => {
  const requestUrl = String(config.url || "").replace(/^\/+/, "");
  const method = String(config.method || "get").toLowerCase();

  return PUBLIC_REQUESTS.some(({ path, methods }) => {
    const matchesPath = requestUrl === path || requestUrl.startsWith(`${path}/`);
    const matchesMethod = !methods || methods.includes(method);

    return matchesPath && matchesMethod;
  });
};

// Crear una instancia de axios
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: REQUEST_TIMEOUT,
});
const transport = axios.create();
api.defaults.adapter = coalesceReads(
  (config) => transport.request({
    ...config,
    adapter: transport.defaults.adapter,
    transformRequest: [],
    transformResponse: [],
  }),
  (config) => api.getUri(config),
);

// Agregar un interceptor de solicitudes
api.interceptors.request.use((config) => {
  const token = getStoredAccessData();
  const publicRequest = isPublicRequest(config);
  const currentRoute = router.currentRoute.value;
  const routePath = currentRoute?.path || window.location.pathname || "";
  const routeTitle =
    currentRoute?.meta?.title || document.title || currentRoute?.name || "";
  const routeModule = routePath.split("/").filter(Boolean)[0] || "home";

  config.headers = config.headers || {};
  config.headers["X-Client-Route"] = routePath;
  config.headers["X-Client-Screen"] = routeTitle;
  config.headers["X-Client-Module"] = routeModule;
  config.headers["Cache-Control"] = "no-store, no-cache, must-revalidate, max-age=0";
  config.headers.Pragma = "no-cache";
  config.headers.Expires = "0";
  config.metadata = {
    ...(config.metadata || {}),
    routePath,
    routeName: currentRoute?.name || "",
    startedAt: Date.now(),
  };

  if (token && hasValidSession()) {
    config.headers.Authorization = `Bearer ${token.token}`;
  } else if (token) {
    clearStoredSession();

    if (publicRequest) {
      return config;
    }

    if (router.currentRoute.value.path !== "/login") {
      router.push("/login");
    }

    return Promise.reject(buildSessionExpiredError());
  }

  return config;
});

// Interceptor de respuesta - Manejo automático de sesiones inválidas
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isSessionExpiredError(error)) {
      return Promise.reject(error);
    }

    const currentPath = router.currentRoute.value?.path || window.location.pathname || "";
    const requestRoutePath = error.config?.metadata?.routePath;
    const isCanceledRequest =
      error.code === "ERR_CANCELED" ||
      error.name === "CanceledError" ||
      error.name === "AbortError" ||
      error.__CANCEL__ === true ||
      String(error.message || "").toLowerCase() === "canceled";

    if (isCanceledRequest) {
      error.isCanceledRequest = true;
    }

    if (requestRoutePath && currentPath && requestRoutePath !== currentPath) {
      error.isStaleRouteRequest = true;
    }

    const isTimeoutError =
      error.code === "ECONNABORTED" ||
      String(error.message || "")
        .toLowerCase()
        .includes("timeout");

    if (isTimeoutError) {
      error.message =
        "El servidor tardó demasiado en responder. Intenta de nuevo.";
    } else if (!error.response && !error.message) {
      error.message = "No se pudo conectar con el backend.";
    }

    const responseStatus = error.response?.status;
    const authStatus = error.response?.data?.status;
    const isAuthenticationFailure =
      responseStatus === 401 &&
      ["Expired token", "Invalid token", "Token not found"].includes(
        authStatus,
      );

    if (isAuthenticationFailure) {
      clearStoredSession();

      // Redirigir al login solo si no estamos ya ahí
      if (router.currentRoute.value.path !== "/login") {
        router.push("/login");
      }

      return Promise.reject(buildSessionExpiredError());
    }

    return Promise.reject(error);
  },
);

export default api;
